import {
    Badge,
    Box,
    Button,
    Checkbox,
    Flex,
    HStack,
    Heading,
    Input,
    Text,
    Tr,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TbBell, TbLayoutGrid, TbShieldLock } from 'react-icons/tb';
import type { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import { UserEntry, getUserStatus, useGetSettings, type Section } from '../../../../hooks/useGetSettings';
import { useEditUser, type EditUserInput } from '../../../../hooks/useEditUser';
import type { UserPermission } from '../../../../hooks/useEditDefaultPermissions';
import type { Permission, Role, WebsiteTab } from '../../../../hooks/useValidateSession';
import type { EnabledNotificationTypesDict } from '../../../../hooks/useGetProfile';
import type { NotificationChannel, NotificationType } from '../../../../hooks/useGetNotifications';
import SessionContext, { isInventoryOrgSession, isInvoicingOrgSession } from '../../../../contexts/SessionContext';
import SearchableSingleSelect from '../../../../components/SearchableSingleSelect';
import LocationSelector from '../../../../components/LocationSelector';
import { POSITION_LABELS, getInvitePositionOptions } from '../../../../utils/invitePositionOptions';
import {
    SettingsActionBar,
    SettingsCard,
    SettingsRow,
    SettingsSubTable,
    SettingsTableTabs,
    SettingsTd,
    SettingsTh,
    useSettingsDetailCrumb,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import { SETTINGS_CONTENT_MAX_WIDTH } from '../../../../components/settings/primitives/constants';
import {
    PERMISSION_LABELS,
    TAB_LABELS,
    copyUserPermission,
    sameUserPermission,
    toggleValue,
    visibleMatrixRows,
} from '../permissions/permissionCatalog';
import {
    dictsEqual,
    resolvePrefs,
    visibleNotificationCategories,
} from '../notificationCatalog';
import NotificationPrefsTable from '../NotificationPrefsTable';

export interface UserDetailSectionProps {
    userId: string;
    users: Array<UserEntry>;
    /** Role defaults from the users section, the base the matrix overlays. */
    defaultPermissions: Record<Role, UserPermission>;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}

/** Admin folds into status: an admin user reads as status "Admin". */
type UserStatusValue = 'active' | 'inactive' | 'admin';

const STATUS_OPTIONS: Array<{ value: UserStatusValue; label: string }> = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'admin', label: 'Admin' },
];

interface UserDraft {
    active: boolean;
    admin: boolean;
    position: Role;
    locationId: Array<string> | 'all';
    /** null = follow the role defaults ("Default" mode in the old modal). */
    customPermissions: UserPermission | null;
    notificationsEnabled: boolean;
    enabledNotificationTypes: EnabledNotificationTypesDict;
}

/** Mirrors the retired EditUserModal: the admin flag wins over any stored set. */
const resolveSavedPermissions = (user: UserEntry): UserPermission | 'admin' | 'default' => {
    if (user.admin === true || user.permissions === 'admin') return 'admin';
    if (user.permissions === 'default' || user.permissions == null) return 'default';
    return user.permissions;
};

const samePermissions = (
    a: UserPermission | 'admin' | 'default',
    b: UserPermission | 'admin' | 'default',
) => {
    if (a === 'admin' || a === 'default' || b === 'admin' || b === 'default') return a === b;
    return sameUserPermission(a, b);
};

const isSameLocationId = (a: Array<string> | 'all', b: Array<string> | 'all'): boolean => {
    if (a === 'all' || b === 'all') return a === b;
    if (a.length !== b.length) return false;
    const other = new Set(b);
    return a.every((id) => other.has(id));
};

const userHasInvoicing = (
    user: UserEntry,
    defaultPermissions: Record<Role, UserPermission>,
    invoicingEnabled: boolean,
): boolean => {
    if (!invoicingEnabled) return false;
    const saved = resolveSavedPermissions(user);
    if (saved === 'admin') return true;
    const permissions =
        saved === 'default'
            ? defaultPermissions[user.position]?.permissions ?? []
            : saved.permissions;
    return permissions.includes('invoicing');
};

const buildDraft = (
    user: UserEntry,
    defaultPermissions: Record<Role, UserPermission>,
    invoicingEnabled: boolean,
): UserDraft => {
    const saved = resolveSavedPermissions(user);
    return {
        active: user.active !== false,
        admin: user.admin === true,
        position: user.position,
        locationId: user.locationId || 'all',
        customPermissions:
            saved === 'admin' || saved === 'default' ? null : copyUserPermission(saved),
        notificationsEnabled: user.notificationsEnabled !== false,
        enabledNotificationTypes: resolvePrefs(user.enabledNotificationTypes, {
            invoicingCapable: userHasInvoicing(user, defaultPermissions, invoicingEnabled),
            position: user.position,
        }),
    };
};

/** What the save payload's `permissions` field would be for this draft. */
const draftPermissions = (draft: UserDraft): UserPermission | 'admin' | 'default' => {
    if (draft.admin) return 'admin';
    return draft.customPermissions ?? 'default';
};

const displayName = (user: UserEntry): string => {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email;
};

type MatrixView = 'tabs' | 'permissions' | 'notifications';

/**
 * Full-page editor for one user, replacing the old EditUserModal. General info
 * is read-only; access fields (status, position, locations) and the
 * per-user permissions matrix are batched into one PATCH /settings/user/edit via the
 * floating action bar, matching how the modal saved.
 */
const UserDetailSection = ({ userId, users, defaultPermissions, refetch }: UserDetailSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const session = useContext(SessionContext);
    const isInvoicingOrg = isInvoicingOrgSession(session);
    const isInventoryOrg = isInventoryOrgSession(session);
    const { data: settingsData } = useGetSettings();
    const editUserMutation = useEditUser();

    const user = users.find((candidate) => candidate._id === userId);
    // Pending invites have no user document, so there is nothing to edit here.
    const isPending = user ? getUserStatus(user) === 'pending' : false;

    const [draft, setDraft] = useState<UserDraft | null>(() =>
        user && !isPending
            ? buildDraft(user, defaultPermissions, session?.invoicingEnabled === true)
            : null,
    );
    const [view, setView] = useState<MatrixView>('tabs');

    useEffect(() => {
        if (!user || isPending) navigate('/settings/users', { replace: true });
    }, [user, isPending, navigate]);

    // The Permissions cell in the users table links straight to the matrix,
    // which sits below the fold, so that entry point scrolls it into view.
    const routerLocation = useLocation();
    const permissionsRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const state = routerLocation.state as { focus?: string } | null;
        if (state?.focus !== 'permissions') return;
        permissionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [routerLocation.state]);

    const positionOptions = useMemo(() => {
        if (isInvoicingOrg) {
            return [{ value: 'invoicing' as Role, label: 'Invoicing' }];
        }
        const options = getInvitePositionOptions({
            includeTechnician: settingsData?.inventoryEnabled === true,
            includeInvoicing: settingsData?.invoicingEnabled === true,
            inventoryOrg: isInventoryOrg,
        });
        // Keep the user's current role selectable even if that feature gate is off.
        if (user?.position && !options.some((opt) => opt.value === user.position)) {
            return [
                ...options.slice(0, -1),
                { value: user.position, label: POSITION_LABELS[user.position] ?? user.position },
                options[options.length - 1],
            ];
        }
        return options;
    }, [isInvoicingOrg, isInventoryOrg, settingsData?.inventoryEnabled, settingsData?.invoicingEnabled, user?.position]);

    const invoicingEnabled = session?.invoicingEnabled === true;
    const invoicingCapable = Boolean(
        invoicingEnabled &&
            user &&
            (draft
                ? draft.admin ||
                  draft.position === 'invoicing' ||
                  (draft.customPermissions?.permissions ??
                      defaultPermissions[draft.position]?.permissions ??
                      []
                  ).includes('invoicing')
                : userHasInvoicing(user, defaultPermissions, invoicingEnabled)),
    );
    const inventoryEnabled = session?.inventoryEnabled === true;
    const rekeyingEnabled = session?.rekeyingEnabled === true;
    const managementSystem = useMemo(() => {
        if (!settingsData) return undefined;
        const sections: Section[] = [
            ...(settingsData.topSection?.sections ?? []),
            ...(settingsData.bottomSection?.sections ?? []),
        ];
        const withMs = sections.find(
            (section): section is Extract<Section, { managementSystem: 'ccc' | 'nex' }> =>
                (section.key === 'ordering' || section.key === 'rekey') &&
                'managementSystem' in section,
        );
        return withMs?.managementSystem;
    }, [settingsData]);
    const organizationMode =
        session?.isAuthenticated === true ? session.organizationMode : undefined;

    const { tabs: visibleTabs, permissions: visiblePermissions } = useMemo(
        () => visibleMatrixRows({ invoicingEnabled, inventoryEnabled }),
        [invoicingEnabled, inventoryEnabled],
    );

    const notificationCategories = useMemo(
        () =>
            visibleNotificationCategories({
                position: (draft ?? user)?.position,
                managementSystem,
                invoicingEnabled,
                invoicingCapable,
                rekeyingEnabled,
                organizationMode,
            }),
        [
            draft,
            user,
            managementSystem,
            invoicingEnabled,
            invoicingCapable,
            rekeyingEnabled,
            organizationMode,
        ],
    );

    const matrixRows: Array<Permission | WebsiteTab> =
        view === 'permissions' ? visiblePermissions : visibleTabs;

    const {
        currentPage: matrixPage,
        setCurrentPage: setMatrixPage,
        totalPages: matrixTotalPages,
        pageItems: matrixPageRows,
    } = useSubTablePagination(matrixRows);

    // Adds "/ {name}" to the settings top bar for as long as this page is open.
    useSettingsDetailCrumb(user && !isPending ? displayName(user) : undefined);

    if (!user || isPending || !draft) return null;

    // Same guard as the table: other admins are view-only unless it's you.
    // Yaxle employees override this so support can manage any customer's users.
    const canEdit =
        !user.admin || user._id === session?.userId || session?.isInternal === true;

    const roleDefault = copyUserPermission(defaultPermissions[draft.position]);

    const statusValue: UserStatusValue = !draft.active
        ? 'inactive'
        : draft.admin
            ? 'admin'
            : 'active';

    const savedStatusBadge = (() => {
        if (user.active === false) return { colorScheme: 'gray', label: 'Inactive' };
        if (user.admin === true) return { colorScheme: 'purple', label: 'Admin' };
        return { colorScheme: 'green', label: 'Active' };
    })();

    const savedNotificationPrefs = resolvePrefs(user.enabledNotificationTypes, {
        invoicingCapable: userHasInvoicing(user, defaultPermissions, invoicingEnabled),
        position: user.position,
    });
    const isDirty =
        draft.admin !== (user.admin === true) ||
        draft.active !== (user.active !== false) ||
        !isSameLocationId(draft.locationId, user.locationId || 'all') ||
        draft.position !== user.position ||
        !samePermissions(draftPermissions(draft), resolveSavedPermissions(user)) ||
        draft.notificationsEnabled !== (user.notificationsEnabled !== false) ||
        !dictsEqual(draft.enabledNotificationTypes, savedNotificationPrefs);

    const handleStatusChange = (value: UserStatusValue) => {
        setDraft((prev) => {
            if (!prev) return prev;
            if (value === 'admin') return { ...prev, active: true, admin: true };
            if (value === 'active') return { ...prev, active: true, admin: false };
            // Inactive also clears admin so the tri-state stays unambiguous.
            return { ...prev, active: false, admin: false };
        });
    };

    const matrixDisabled = !canEdit || draft.admin || !draft.active;

    const isChecked = (item: Permission | WebsiteTab): boolean => {
        if (draft.admin) return true;
        const effective = draft.customPermissions ?? roleDefault;
        return view === 'tabs'
            ? effective.tabs.includes(item as WebsiteTab)
            : effective.permissions.includes(item as Permission);
    };

    const handleToggle = (item: Permission | WebsiteTab) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const base = prev.customPermissions ?? copyUserPermission(defaultPermissions[prev.position]);
            const next: UserPermission =
                view === 'tabs'
                    ? { ...base, tabs: toggleValue(base.tabs, item as WebsiteTab) }
                    : { ...base, permissions: toggleValue(base.permissions, item as Permission) };
            // A set identical to the role defaults is just "Default" again.
            const fallsBackToDefault = sameUserPermission(
                next,
                copyUserPermission(defaultPermissions[prev.position]),
            );
            return { ...prev, customPermissions: fallsBackToDefault ? null : next };
        });
    };

    // Header check-all: sets every row visible in the current view, leaving
    // feature-gated (hidden) tabs/permissions untouched, with the same
    // falls-back-to-default collapse as single toggles.
    const handleSetAll = (enabled: boolean) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const base =
                prev.customPermissions ?? copyUserPermission(defaultPermissions[prev.position]);
            let next: UserPermission;
            if (view === 'tabs') {
                const kept = base.tabs.filter((tab) => !visibleTabs.includes(tab));
                next = { ...base, tabs: enabled ? [...kept, ...visibleTabs] : kept };
            } else {
                const kept = base.permissions.filter(
                    (permission) => !visiblePermissions.includes(permission),
                );
                next = { ...base, permissions: enabled ? [...kept, ...visiblePermissions] : kept };
            }
            const fallsBackToDefault = sameUserPermission(
                next,
                copyUserPermission(defaultPermissions[prev.position]),
            );
            return { ...prev, customPermissions: fallsBackToDefault ? null : next };
        });
    };

    const handleNotificationChannelToggle = (
        types: NotificationType[],
        channel: NotificationChannel,
        isEnabled: boolean,
    ) => {
        setDraft((prev) => {
            if (!prev) return prev;
            const next = { ...prev.enabledNotificationTypes };
            for (const type of types) {
                const current = next[type] ?? [];
                next[type] = isEnabled
                    ? current.includes(channel)
                        ? current
                        : [...current, channel]
                    : current.filter((c) => c !== channel);
            }
            return { ...prev, enabledNotificationTypes: next };
        });
    };

    const handleSave = async () => {
        // Diff payload, exactly like the retired modal: only changed fields go out.
        const payload: EditUserInput = { userId: user._id };
        if (draft.admin !== (user.admin === true)) payload.admin = draft.admin;
        if (draft.active !== (user.active !== false)) payload.active = draft.active;
        if (!isSameLocationId(draft.locationId, user.locationId || 'all')) {
            payload.locationId = draft.locationId;
        }
        if (draft.position !== user.position) payload.position = draft.position;
        const nextPermissions = draftPermissions(draft);
        if (!samePermissions(nextPermissions, resolveSavedPermissions(user))) {
            payload.permissions = nextPermissions;
        }
        if (draft.notificationsEnabled !== (user.notificationsEnabled !== false)) {
            payload.notificationsEnabled = draft.notificationsEnabled;
        }
        if (!dictsEqual(draft.enabledNotificationTypes, savedNotificationPrefs)) {
            payload.enabledNotificationTypes = draft.enabledNotificationTypes;
        }

        try {
            await editUserMutation.mutateAsync(payload);
            toast({
                title: 'User updated successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            refetch();
        } catch (error: unknown) {
            toast({
                title: 'Could not update user. Please try again!',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const readOnlyInputProps = {
        size: 'sm' as const,
        // Match the 36px height of the dropdowns and the location selector.
        h: '36px',
        w: '400px',
        maxW: '100%',
        borderColor: 'gray.200',
        isReadOnly: true,
        bg: 'gray.50',
        color: 'gray.600',
        cursor: 'not-allowed',
    };

    const matrixLabels: Record<string, string> = view === 'tabs' ? TAB_LABELS : PERMISSION_LABELS;

    const allMatrixChecked = matrixRows.length > 0 && matrixRows.every((item) => isChecked(item));
    const someMatrixChecked = matrixRows.some((item) => isChecked(item));

    const permissionsBadge = draft.admin
        ? { colorScheme: 'purple', label: 'Admin' }
        : draft.customPermissions
            ? { colorScheme: 'blue', label: 'Custom' }
            : { colorScheme: 'gray', label: 'Default' };
    const notificationsPaused = draft.notificationsEnabled === false;
    const notificationsBadge = notificationsPaused
        ? { colorScheme: 'gray', label: 'Disabled' }
        : { colorScheme: 'green', label: 'Enabled' };
    const isNotificationsView = view === 'notifications';
    const notificationCount = notificationCategories.reduce(
        (sum, category) => sum + category.types.length,
        0,
    );

    return (
        <Flex direction="column" h="100%" minH={0} w="100%" position="relative">
            <Box flex={1} minH={0} overflowY="auto" pt={6} pb={24}>
                <Box px={8}>
                    <Box maxW={SETTINGS_CONTENT_MAX_WIDTH} mx="auto">
                        <Flex justify="space-between" align="flex-start" gap={4} pb={6}>
                            <Box>
                                <HStack spacing={3}>
                                    <Heading size="md" color="gray.800">
                                        {displayName(user)}
                                    </Heading>
                                    <Badge colorScheme={savedStatusBadge.colorScheme} variant="subtle">
                                        {savedStatusBadge.label}
                                    </Badge>
                                </HStack>
                            </Box>
                        </Flex>

                        <VStack spacing={6} align="stretch">
                            {!canEdit && (
                                <Text fontSize="sm" color="gray.500">
                                    Other administrators are view-only.
                                </Text>
                            )}

                            <SettingsCard title="General">
                                <SettingsRow
                                    label="Name"
                                    control={
                                        <Input
                                            {...readOnlyInputProps}
                                            value={
                                                `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '—'
                                            }
                                        />
                                    }
                                />
                                <SettingsRow
                                    label="Email"
                                    description="Used for sign in and notifications."
                                    control={<Input {...readOnlyInputProps} value={user.email || '—'} />}
                                />
                                <SettingsRow
                                    label="Username"
                                    control={<Input {...readOnlyInputProps} value={user.username || '—'} />}
                                />
                            </SettingsCard>

                            <SettingsCard title="Access">
                                <SettingsRow
                                    label="Status"
                                    description="Admins have full access to every tab and permission."
                                    control={
                                        <SearchableSingleSelect
                                            width="200px"
                                            options={STATUS_OPTIONS}
                                            value={statusValue}
                                            isDisabled={!canEdit}
                                            onChange={(next) => handleStatusChange(next as UserStatusValue)}
                                        />
                                    }
                                />
                                <SettingsRow
                                    label="Position"
                                    description="Determines the default tabs and permissions."
                                    control={
                                        <SearchableSingleSelect
                                            width="200px"
                                            options={positionOptions}
                                            value={draft.position}
                                            placeholder="Select position"
                                            isDisabled={!canEdit || !draft.active || isInvoicingOrg}
                                            onChange={(next) =>
                                                setDraft((prev) =>
                                                    prev ? { ...prev, position: next as Role } : prev,
                                                )
                                            }
                                        />
                                    }
                                />
                                <SettingsRow
                                    label="Locations"
                                    description="Which shops this user can see and work in."
                                    control={
                                        canEdit && draft.active ? (
                                            <LocationSelector
                                                selectedLocations={draft.locationId}
                                                onLocationChange={(locationIds) =>
                                                    setDraft((prev) =>
                                                        prev ? { ...prev, locationId: locationIds } : prev,
                                                    )
                                                }
                                                width="200px"
                                            />
                                        ) : (
                                            <Input
                                                {...readOnlyInputProps}
                                                w="200px"
                                                value={
                                                    draft.locationId === 'all'
                                                        ? 'All Locations'
                                                        : `${draft.locationId.length} location${draft.locationId.length === 1 ? '' : 's'}`
                                                }
                                            />
                                        )
                                    }
                                />
                            </SettingsCard>
                        </VStack>
                    </Box>
                </Box>

                {/* Per-user permissions matrix, in the same column as the cards. */}
                <Box ref={permissionsRef} px={8} mt={12}>
                    <Box maxW={SETTINGS_CONTENT_MAX_WIDTH} mx="auto">
                        <Flex
                            pb={4}
                            justify="space-between"
                            align="flex-start"
                            gap={4}
                        >
                            <Box>
                                <HStack spacing={2.5}>
                                    <Heading size="sm" color="gray.800">
                                        {isNotificationsView ? 'Notifications' : 'Permissions'}
                                    </Heading>
                                    <Badge
                                        colorScheme={
                                            isNotificationsView
                                                ? notificationsBadge.colorScheme
                                                : permissionsBadge.colorScheme
                                        }
                                        variant="subtle"
                                    >
                                        {isNotificationsView
                                            ? notificationsBadge.label
                                            : permissionsBadge.label}
                                    </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.500" mt={1}>
                                    {isNotificationsView
                                        ? notificationsPaused
                                            ? 'In-app and email alerts are paused for this user. Enable to edit channels.'
                                            : 'In-app and email alerts this user receives. Disabling pauses every notification.'
                                        : draft.admin
                                            ? 'Admins have every tab and permission; change status to customize.'
                                            : 'Checked values come from the role defaults. Changing any box gives this user custom permissions.'}
                                </Text>
                            </Box>
                            {isNotificationsView
                                ? canEdit && (
                                    <Button
                                        size="sm"
                                        variant="outlineGray"
                                        flexShrink={0}
                                        onClick={() =>
                                            setDraft((prev) =>
                                                prev
                                                    ? {
                                                        ...prev,
                                                        notificationsEnabled: !prev.notificationsEnabled,
                                                    }
                                                    : prev,
                                            )
                                        }
                                    >
                                        {notificationsPaused ? 'Enable' : 'Disable'}
                                    </Button>
                                )
                                : !draft.admin && draft.customPermissions && canEdit && (
                                    <Button
                                        size="sm"
                                        variant="outlineGray"
                                        flexShrink={0}
                                        onClick={() =>
                                            setDraft((prev) =>
                                                prev ? { ...prev, customPermissions: null } : prev,
                                            )
                                        }
                                    >
                                        Reset to role defaults
                                    </Button>
                                )}
                        </Flex>

                        <SettingsTableTabs
                            tabs={[
                                {
                                    id: 'tabs',
                                    label: 'Tabs',
                                    icon: TbLayoutGrid as React.ElementType,
                                    count: visibleTabs.length,
                                },
                                {
                                    id: 'permissions',
                                    label: 'Permissions',
                                    icon: TbShieldLock as React.ElementType,
                                    count: visiblePermissions.length,
                                },
                                {
                                    id: 'notifications',
                                    label: 'Notifications',
                                    icon: TbBell as React.ElementType,
                                    count: notificationCount,
                                },
                            ]}
                            value={view}
                            onChange={(id) => setView(id as MatrixView)}
                            px={0}
                        />

                        {isNotificationsView ? (
                            <NotificationPrefsTable
                                prefs={draft.enabledNotificationTypes}
                                isPaused={notificationsPaused}
                                isDisabled={!canEdit || !draft.active}
                                categories={notificationCategories}
                                variant="embedded"
                                onChannelToggle={handleNotificationChannelToggle}
                            />
                        ) : (
                        <SettingsSubTable
                            currentPage={matrixPage}
                            totalPages={matrixTotalPages}
                            onPageChange={setMatrixPage}
                            head={
                                <>
                                    <SettingsTh minW="220px">
                                        {view === 'tabs' ? 'Tab' : 'Permission'}
                                    </SettingsTh>
                                    <SettingsTh>
                                        <HStack spacing={1.5}>
                                            <Checkbox
                                                size="sm"
                                                colorScheme="partbay-blue"
                                                isChecked={allMatrixChecked}
                                                isIndeterminate={!allMatrixChecked && someMatrixChecked}
                                                isDisabled={matrixDisabled}
                                                onChange={(e) => handleSetAll(e.target.checked)}
                                                aria-label={`Toggle all ${view === 'tabs' ? 'tabs' : 'permissions'}`}
                                            />
                                            <Box as="span">Enabled</Box>
                                        </HStack>
                                    </SettingsTh>
                                </>
                            }
                        >
                            {matrixPageRows.map((item) => (
                                <Tr key={item} _hover={{ bg: 'gray.50' }}>
                                    <SettingsTd fontWeight="medium" color="gray.800" whiteSpace="nowrap">
                                        {matrixLabels[item] ?? item}
                                    </SettingsTd>
                                    <SettingsTd>
                                        <Checkbox
                                            colorScheme="partbay-blue"
                                            isChecked={isChecked(item)}
                                            isDisabled={matrixDisabled}
                                            onChange={() => handleToggle(item)}
                                            aria-label={matrixLabels[item] ?? item}
                                        />
                                    </SettingsTd>
                                </Tr>
                            ))}
                        </SettingsSubTable>
                        )}
                    </Box>
                </Box>
            </Box>

            <SettingsActionBar
                isDirty={isDirty}
                isSaving={editUserMutation.isPending}
                onSave={handleSave}
                onDiscard={() =>
                    setDraft(buildDraft(user, defaultPermissions, invoicingEnabled))
                }
            />
        </Flex>
    );
};

export default UserDetailSection;
